import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button, Checkbox, Modal, RobbieMark, Table, TableColumn, Tooltip } from '../../ui';
import { DiscoveryRefusal } from '../../../types/discovery.types';
import { formatCredits } from '../../../utils/credits';
import { CreditRequestState } from '../../../hooks/useCreditRequest';
import { PickerComponent, useCreditsPicker } from '../../../hooks/useCreditsPicker';
import { creditRequestContext } from '../../../utils/creditRequest';
import CreditRequestOutcome from '../../credits/CreditRequestOutcome';
import CreditRequestTrigger from '../../credits/CreditRequestTrigger';

/**
 * The credits picker. Robbie's sentence leads; the table is the buyer's
 * choice, ticked in cobalt and capped at what the balance covers; the lines
 * left unticked are removed from the BOM.
 *
 * Two moments use it:
 * - `exit` (default) — the buyer pressed Continue on step 2 and the 402 said
 *   the org can afford some of the lines. Start charges and starts discovery.
 * - `gate` — the buyer just ENTERED step 2 with a balance that cannot cover
 *   every line. Nothing is charged: the lines are kept, the step unlocks, and
 *   discovery is paid for at the exit. It cannot be dismissed (the step stays
 *   closed until it is answered); the way out is "Back to upload". Lines
 *   already paid for are ticked, locked and outside the cap. With nothing
 *   affordable and nothing paid for there is nothing to pick, so the table
 *   gives way to Request more credits.
 */

export interface CreditsPickerProps {
  open: boolean;
  refusal: DiscoveryRefusal;
  components: PickerComponent[];
  variant?: 'exit' | 'gate';
  /** Gate only: lines already paid for. They stay, free, outside the cap. */
  paidComponentIds?: string[];
  /** Display RFQ id for the credit request, e.g. RFQ-003. */
  rfqLabel?: string;
  /** "Request more credits": the state from `useCreditRequest` (`ConnectedCreditsPicker` supplies it). */
  creditRequest: CreditRequestState;
  starting?: boolean;
  /** A failed Start that left the picker open. */
  error?: string | null;
  /** The lines to keep: the ticked ones plus any locked (paid for) ones. */
  onStart: (componentIds: string[]) => void;
  /** Exit: Cancel. Unused by the gate, which cannot be cancelled. */
  onCancel?: () => void;
  /** Gate: "Back to upload". */
  onBack?: () => void;
}

const COPY = {
  exit: {
    title: 'Pick the components to start with',
    rowLabel: (name: string) => `Start discovery for ${name}`,
    action: (count: number, cost: string) => (count === 0 ? 'Start discovery' : `Start discovery for ${count} · ${cost} credits`),
    busy: 'Starting discovery…',
    capHint: (cap: number) => `You can start ${cap} with your current balance`,
    empty: "I can't see the lines of this BOM right now. Cancel, reopen the BOM, then press Continue again.",
  },
  gate: {
    title: 'Pick the components to take forward',
    rowLabel: (name: string) => `Take ${name} forward`,
    action: (count: number) => (count === 0 ? 'Take forward' : `Take ${count} forward`),
    busy: 'Keeping these lines…',
    capHint: (cap: number) => `You can take ${cap} more with your current balance`,
    empty: "I can't see the lines of this BOM right now. Go back to upload, then open this step again.",
  },
} as const;

const LOCKED_HINT = 'Already paid for, so it stays';

const dash = <span className="text-ink-muted">—</span>;

const buildColumns = (
  isChecked: (id: string) => boolean,
  hintFor: (id: string) => string | null,
  toggle: (id: string) => void,
  rowLabel: (name: string) => string
): TableColumn<PickerComponent & { componentId: string }>[] => [
  {
    key: 'pick',
    width: '22px',
    render: (row) => {
      const hint = hintFor(row.componentId);
      return (
        // The row toggles on click; the box must not toggle it a second time.
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Tooltip content={hint ?? ''} disabled={!hint}>
            <Checkbox
              checked={isChecked(row.componentId)}
              disabled={hint !== null}
              onCheckedChange={() => toggle(row.componentId)}
              aria-label={rowLabel(row.name || 'this component')}
            />
          </Tooltip>
        </span>
      );
    },
  },
  {
    key: 'no',
    header: '#',
    width: '38px',
    mono: true,
    render: (_row, index) => <span className="text-ink-muted">{String(index + 1).padStart(2, '0')}</span>,
  },
  {
    key: 'component',
    header: 'Component',
    render: (row) => {
      const identifier = row.partNumber || row.mpn;
      return (
        <span className="truncate font-semibold text-ink max-md:whitespace-normal">
          {row.name || dash}
          {identifier && <span className="font-mono text-meta font-normal text-ink-muted"> · {identifier}</span>}
        </span>
      );
    },
  },
  {
    key: 'qty',
    header: 'Qty',
    width: '60px',
    mono: true,
    render: (row) => ((row.quantity ?? '') !== '' ? row.quantity : dash),
  },
];

export const CreditsPicker: React.FC<CreditsPickerProps> = ({
  open,
  refusal,
  components,
  variant = 'exit',
  paidComponentIds,
  rfqLabel,
  creditRequest,
  starting = false,
  error,
  onStart,
  onCancel,
  onBack,
}) => {
  const gate = variant === 'gate';
  const copy = COPY[variant];
  const cap = refusal.affordable ?? 0;
  const { rows, selected, locked, kept, toggle, atCap } = useCreditsPicker(components, cap, open, paidComponentIds);

  // Nothing to tick: no line is affordable and none is already paid for.
  const pickable = cap > 0 || locked.size > 0;
  const count = selected.length;
  const cost = formatCredits(count * refusal.rate);
  const hintFor = (id: string) => {
    if (locked.has(id)) return LOCKED_HINT;
    return atCap && !selected.includes(id) ? copy.capHint(cap) : null;
  };
  const columns = buildColumns(
    (id) => locked.has(id) || selected.includes(id),
    hintFor,
    toggle,
    copy.rowLabel
  );
  const paidNote = locked.size > 0 ? ` + ${locked.size} paid for` : '';
  const counter = gate
    ? `${count} of ${cap} selected${paidNote} · ${cost} credits at discovery`
    : `${count} of ${cap} selected · ${cost} credits`;

  const requestControl = (
    <CreditRequestTrigger
      state={creditRequest}
      onRequest={() => creditRequest.request(creditRequestContext(refusal, rfqLabel))}
      variant={pickable ? 'secondary' : 'primary'}
      disabled={starting}
    />
  );
  const leave = gate ? (
    <Button variant="ghost" onClick={onBack} disabled={starting}>
      Back to upload
    </Button>
  ) : (
    <Button variant="ghost" onClick={onCancel} disabled={starting}>
      Cancel
    </Button>
  );

  return (
    <Modal
      open={open}
      onClose={starting || !onCancel ? () => undefined : onCancel}
      dismissable={!gate}
      size="xl"
      title={copy.title}
      footer={
        <div className="flex w-full flex-wrap items-center gap-s2">
          {pickable && (
            <span className="mr-auto font-mono text-mono-data text-ink-secondary" aria-live="polite">
              {counter}
            </span>
          )}
          {!pickable && <span className="mr-auto" />}
          {leave}
          {requestControl}
          {pickable && (
            <Button
              onClick={() => onStart(kept)}
              disabled={kept.length === 0}
              loading={starting}
              loadingLabel={copy.busy}
            >
              {gate ? COPY.gate.action(kept.length) : COPY.exit.action(count, cost)}
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-s5">
        <div className="flex items-start gap-s4 rounded-card border border-robbie-border bg-ai-panel px-[14px] py-[10px]">
          <RobbieMark size="xs" aria-label="Robbie" className="mt-[1px] shrink-0" />
          <p className="m-0 min-w-0 text-body-sm text-ink-body">{refusal.message}</p>
        </div>

        {pickable && (
          <>
            <Table
              aria-label={`BOM components — pick up to ${cap}`}
              columns={columns}
              rows={rows}
              rowKey={(row) => row.componentId}
              density="compact"
              selectedKeys={kept}
              onRowClick={(row) => toggle(row.componentId)}
              stickyHeader
              maxHeight="44vh"
              empty={copy.empty}
            />
            <p className="m-0 text-body-sm text-ink-secondary">The lines you don't pick are removed from this BOM.</p>
          </>
        )}

        <CreditRequestOutcome state={creditRequest} />

        {error && (
          <p role="alert" className="m-0 flex items-start gap-s2 text-body-sm text-danger-ink">
            <AlertTriangle className="mt-[2px] h-[13px] w-[13px] shrink-0" />
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default CreditsPicker;
